import { useState } from 'react'
import { Button, Select, Table, Progress, Tag, App } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { vendorPerformanceApi } from '@/features/procurement/api'
import { plantsApi } from '@/features/masters/plants/api'
import { suppliersApi } from '@/features/gate/api'
import type { VendorPerformance } from '@/shared/types/procurement'
import PageHeader from '@/shared/components/PageHeader'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function ratingColor(score: number): string {
  if (score >= 80) return 'green'
  if (score >= 60) return 'orange'
  return 'red'
}

export default function VendorPerformancePage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [plantFilter, setPlantFilter] = useState<string | undefined>()
  const [supplierFilter, setSupplierFilter] = useState<string | undefined>()
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear())

  const { data, isLoading } = useQuery({
    queryKey: ['vendor-performance', plantFilter, supplierFilter, yearFilter],
    queryFn: () => vendorPerformanceApi.list({ plant_id: plantFilter, supplier_id: supplierFilter, year: yearFilter }),
  })
  const { data: plantsData } = useQuery({ queryKey: ['plants-all'], queryFn: () => plantsApi.list({ page: 1, page_size: 500 }) })
  const { data: suppliers } = useQuery({ queryKey: ['suppliers-all'], queryFn: () => suppliersApi.list({ limit: 500 }) })

  const plantOptions = plantsData?.items.map(p => ({ label: p.name, value: p.id })) ?? []
  const supplierOptions = suppliers?.items?.map((s: any) => ({ label: s.supplier_name, value: s.id })) ?? []
  const yearOptions = Array.from({ length: 4 }, (_, i) => {
    const y = new Date().getFullYear() - 1 + i
    return { label: String(y), value: y }
  })

  const recalcMutation = useMutation({
    mutationFn: ({ supplier_id, plant_id, year, month }: Record<string, unknown>) =>
      vendorPerformanceApi.recalculate({ supplier_id, plant_id, year, month }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['vendor-performance'] }); message.success('Recalculated') },
    onError: () => message.error('Recalculate failed'),
  })

  const columns = [
    { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name' },
    { title: 'Period', key: 'period', width: 90,
      render: (_: unknown, r: VendorPerformance) => `${MONTHS[r.period_month - 1]} ${r.period_year}` },
    { title: 'POs', dataIndex: 'total_pos', key: 'total_pos', width: 60 },
    { title: 'On-Time %', dataIndex: 'on_time_delivery_pct', key: 'on_time_delivery_pct', width: 120,
      render: (v: string) => <Progress percent={Number(v)} size="small" /> },
    { title: 'Rejection %', dataIndex: 'rejection_pct', key: 'rejection_pct', width: 110,
      render: (v: string) => <Tag color={Number(v) < 5 ? 'green' : 'red'}>{Number(v).toFixed(1)}%</Tag> },
    { title: 'Quality Score', dataIndex: 'quality_score', key: 'quality_score', width: 120,
      render: (v: string) => <Progress percent={Number(v)} size="small" strokeColor={ratingColor(Number(v))} /> },
    { title: 'Overall Rating', dataIndex: 'overall_rating', key: 'overall_rating', width: 120,
      render: (v: string) => <Tag color={ratingColor(Number(v))}>{Number(v).toFixed(1)} / 100</Tag> },
    {
      title: '', key: 'recalc', width: 80,
      render: (_: unknown, r: VendorPerformance) => (
        <a onClick={() => recalcMutation.mutate({
          supplier_id: r.supplier_id, plant_id: r.plant_id,
          year: r.period_year, month: r.period_month,
        })}>Refresh</a>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Vendor Performance"
        subtitle="Monthly supplier scorecard — delivery, quality, pricing"
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Select allowClear placeholder="Plant" options={plantOptions} onChange={v => setPlantFilter(v)} style={{ width: 180 }} />
        <Select allowClear placeholder="Supplier" options={supplierOptions} onChange={v => setSupplierFilter(v)} showSearch optionFilterProp="label" style={{ width: 200 }} />
        <Select options={yearOptions} value={yearFilter} onChange={v => setYearFilter(v)} style={{ width: 100 }} />
      </div>

      <Table rowKey="id" columns={columns} dataSource={data} loading={isLoading} pagination={false} scroll={{ x: 900 }} />
    </div>
  )
}
